import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Ticket as TicketIcon,
  CheckCircle2,
  Clock,
  Search,
  RefreshCw,
  ExternalLink,
  Loader2,
  AlertCircle,
  Mail,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Trash,
} from "lucide-react";
import { ErrorTost, SuccessTost } from "../../utils/ToastHelper";
import { Apirequests } from "../../utils/ApiRequests";

function fmtDateTime(d) {
  if (!d) return "—";
  const date = new Date(d);
  if (isNaN(date)) return "—";
  return date.toLocaleString(undefined, {
    day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
  });
}

// Builds the page list with ellipses, e.g. [1, "…", 4, 5, 6, "…", 12]
function getPageList(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const pages = [];
  const left = Math.max(2, current - 1);
  const right = Math.min(total - 1, current + 1);
  pages.push(1);
  if (left > 2) pages.push("…l");
  for (let i = left; i <= right; i++) pages.push(i);
  if (right < total - 1) pages.push("…r");
  pages.push(total);
  return pages;
}

function StatusBadge({ checkedIn }) {
  return checkedIn ? (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 px-2.5 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-inset ring-emerald-200">
      <CheckCircle2 className="h-3.5 w-3.5" /> Checked in
    </span>
  ) : (
    <span className="inline-flex items-center gap-1.5 rounded-full bg-amber-50 px-2.5 py-1 text-xs font-semibold text-amber-700 ring-1 ring-inset ring-amber-200">
      <Clock className="h-3.5 w-3.5" /> Pending
    </span>
  );
}

function StatCard({ icon: Icon, label, value, tint }) {
  return (
    <div className="flex items-center gap-3 rounded-xl border border-slate-200 bg-white p-4">
      <div className={`flex h-11 w-11 items-center justify-center rounded-lg ${tint}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div>
        <div className="text-2xl font-bold leading-none text-slate-800">{value}</div>
        <div className="mt-1 text-xs font-medium uppercase tracking-wide text-slate-500">{label}</div>
      </div>
    </div>
  );
}

export default function TicketsTable() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState("all"); // all | checked | pending
  const [sort, setSort] = useState("newest"); // newest | name | status

  // pagination
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const apiUrl = `https://www.silvamethod.in/manage/api/v1/tickets`;
  const token = localStorage.getItem("token");

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch(apiUrl, {
        headers: {
          authorization: `Bearer ${token}`,
        },
      });
      const data = await res.json();
      const list = Array.isArray(data) ? data : data.tickets || data.data || [];
      setTickets(list);
    } catch (e) {
      setError("Could not load tickets. Check the endpoint / key.");
    } finally {
      setLoading(false);
    }
  }, [apiUrl, token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const stats = useMemo(() => {
    const total = tickets.length;
    const checked = tickets.filter((t) => t.checkedIn).length;
    return { total, checked, pending: total - checked };
  }, [tickets]);

  const filtered = useMemo(() => {
    let list = [...tickets];
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((t) =>
        [t.name, t.email, t.code, t.eventName].some((v) =>
          String(v || "").toLowerCase().includes(q)
        )
      );
    }
    if (status === "checked") list = list.filter((t) => t.checkedIn);
    if (status === "pending") list = list.filter((t) => !t.checkedIn);

    if (sort === "name") {
      list.sort((a, b) => String(a.name || "").localeCompare(String(b.name || "")));
    } else if (sort === "status") {
      list.sort((a, b) => Number(b.checkedIn) - Number(a.checkedIn));
    } else {
      list.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
    }
    return list;
  }, [tickets, query, status, sort]);

  // ---- pagination math ----
  const pageCount = Math.max(1, Math.ceil(filtered.length / pageSize));

  // reset to first page whenever the result set changes
  useEffect(() => {
    setPage(1);
  }, [query, status, sort, pageSize, tickets]);

  // clamp if the current page no longer exists
  useEffect(() => {
    if (page > pageCount) setPage(pageCount);
  }, [page, pageCount]);

  const paged = useMemo(
    () => filtered.slice((page - 1) * pageSize, page * pageSize),
    [filtered, page, pageSize]
  );

  const start = filtered.length === 0 ? 0 : (page - 1) * pageSize + 1;
  const end = Math.min(page * pageSize, filtered.length);
  const pageList = getPageList(page, pageCount);

  const statusTabs = [
    { id: "all", label: "All" },
    { id: "checked", label: "Checked in" },
    { id: "pending", label: "Pending" },
  ];

  const handleDelete= async(id)=>{
    if(!id) return ErrorTost("id missing");
    try {
        const res= await Apirequests(`tickets/delete/${id}`,'DELETE',{},{
            authorization: `Bearer ${token}`
        });
        if(res?.success){
            await fetchData();
            await SuccessTost(res?.message || "Lead deleted successfully.");
        }else{
            ErrorTost(res?.message || "Something went wrong.")
        }
    } catch (error) {
        ErrorTost(error?.message || "Internal server error.")
    }
  }

  return (
    <div className="min-h-screen bg-slate-50 p-4 sm:p-6">
      <div className="mx-auto max-w-6xl">
        {/* Header */}
        <div className="mb-5 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-indigo-600 text-white">
              <TicketIcon className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Tickets</h1>
              <p className="text-sm text-slate-500">Manage attendees and check-in status</p>
            </div>
          </div>
          <button
            onClick={fetchData}
            disabled={loading}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:opacity-50"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
            <span className="hidden sm:inline">Refresh</span>
          </button>
        </div>

        {/* Stats */}
        <div className="mb-5 grid grid-cols-1 gap-3 sm:grid-cols-3">
          <StatCard icon={TicketIcon} label="Total tickets" value={stats.total} tint="bg-indigo-50 text-indigo-600" />
          <StatCard icon={CheckCircle2} label="Checked in" value={stats.checked} tint="bg-emerald-50 text-emerald-600" />
          <StatCard icon={Clock} label="Pending" value={stats.pending} tint="bg-amber-50 text-amber-600" />
        </div>

        {/* Controls */}
        <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <div className="relative w-full sm:max-w-xs">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search name, email, or code"
              className="w-full rounded-lg border border-slate-200 bg-white py-2 pl-9 pr-3 text-sm text-slate-700 outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100"
            />
          </div>

          <div className="flex items-center gap-2">
            <div className="inline-flex rounded-lg border border-slate-200 bg-white p-0.5">
              {statusTabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => setStatus(tab.id)}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold transition ${
                    status === tab.id
                      ? "bg-indigo-600 text-white"
                      : "text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-600 outline-none focus:border-indigo-400"
            >
              <option value="newest">Newest</option>
              <option value="name">Name A–Z</option>
              <option value="status">Status</option>
            </select>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-medium text-red-700">
            <AlertCircle className="h-4 w-4" /> {error}
          </div>
        )}

        {/* Card: table (desktop) + cards (mobile) */}
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white">
          {loading ? (
            <div className="flex items-center justify-center gap-2 p-12 text-slate-500">
              <Loader2 className="h-5 w-5 animate-spin" /> Loading tickets…
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 p-12 text-center text-slate-500">
              <TicketIcon className="h-8 w-8 text-slate-300" />
              <p className="font-medium">No tickets found</p>
              <p className="text-sm">Try a different search or filter.</p>
            </div>
          ) : (
            <>
              {/* Desktop table */}
              <div className="hidden md:block">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-200 bg-slate-50 text-xs uppercase tracking-wide text-slate-500">
                      <th className="px-5 py-3 font-semibold">Guest</th>
                      <th className="px-5 py-3 font-semibold">Code</th>
                      <th className="px-5 py-3 font-semibold">Event</th>
                      <th className="px-5 py-3 font-semibold">Status</th>
                      <th className="px-5 py-3 font-semibold">Checked in</th>
                      <th className="px-5 py-3 font-semibold text-right">Ticket</th>
                      <th className="px-5 py-3 font-semibold text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paged.map((t) => (
                      <tr key={t.code} className="transition hover:bg-slate-50/70">
                        <td className="px-5 py-3.5">
                          <div className="font-semibold text-slate-800">{t.name || "—"}</div>
                          <div className="text-xs text-slate-500">{t.email || "—"}</div>
                        </td>
                        <td className="px-5 py-3.5">
                          <span className="rounded bg-slate-100 px-2 py-1 font-mono text-xs text-slate-700">
                            {t.code}
                          </span>
                        </td>
                        <td className="px-5 py-3.5 text-slate-600">{t.eventName || "—"}</td>
                        <td className="px-5 py-3.5"><StatusBadge checkedIn={t.checkedIn} /></td>
                        <td className="px-5 py-3.5 text-slate-600">{fmtDateTime(t.checkedInAt)}</td>
                        <td className="px-5 py-3.5 text-right">
                          {t.ticketUrl ? (
                            <a
                              href={t.ticketUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="inline-flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-800"
                            >
                              View <ExternalLink className="h-3.5 w-3.5" />
                            </a>
                          ) : (
                            <span className="text-slate-400">—</span>
                          )}
                        </td>
                         <td className="px-5 py-3.5 cursor-pointer"><Trash className="text-red-500" onClick={()=>handleDelete(t?._id)}/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards */}
              <div className="divide-y divide-slate-100 md:hidden">
                {paged.map((t) => (
                  <div key={t.code} className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0">
                        <div className="truncate font-semibold text-slate-800">{t.name || "—"}</div>
                        <div className="mt-0.5 flex items-center gap-1 truncate text-xs text-slate-500">
                          <Mail className="h-3 w-3 shrink-0" /> {t.email || "—"}
                        </div>
                      </div>
                      <StatusBadge checkedIn={t.checkedIn} />
                    </div>

                    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-xs text-slate-600">
                      <span className="rounded bg-slate-100 px-2 py-1 font-mono text-slate-700">{t.code}</span>
                      {t.checkedIn && (
                        <span className="inline-flex items-center gap-1">
                          <Calendar className="h-3 w-3" /> {fmtDateTime(t.checkedInAt)}
                        </span>
                      )}
                    </div>

                    <div className="mt-2 flex items-center justify-between">
                      <span className="truncate text-xs text-slate-500">{t.eventName || "—"}</span>
                      {t.ticketUrl && (
                        <a
                          href={t.ticketUrl}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex shrink-0 items-center gap-1.5 text-sm font-semibold text-indigo-600"
                        >
                          View <ExternalLink className="h-3.5 w-3.5" />
                        </a>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Pagination footer */}
              <div className="flex flex-col items-center justify-between gap-3 border-t border-slate-200 px-5 py-3 sm:flex-row">
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <span className="hidden sm:inline">Rows per page</span>
                  <select
                    value={pageSize}
                    onChange={(e) => setPageSize(Number(e.target.value))}
                    className="rounded-md border border-slate-200 bg-white px-2 py-1 text-sm font-medium text-slate-600 outline-none focus:border-indigo-400"
                  >
                    {[10, 25, 50, 100].map((n) => (
                      <option key={n} value={n}>{n}</option>
                    ))}
                  </select>
                  <span className="ml-1">
                    {start}–{end} of {filtered.length}
                  </span>
                </div>

                <div className="flex items-center gap-1">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Previous page"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>

                  {pageList.map((p) =>
                    typeof p === "string" ? (
                      <span key={p} className="px-1.5 text-sm text-slate-400">…</span>
                    ) : (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`inline-flex h-8 min-w-8 items-center justify-center rounded-md px-2 text-sm font-semibold transition ${
                          p === page
                            ? "bg-indigo-600 text-white"
                            : "border border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                        }`}
                      >
                        {p}
                      </button>
                    )
                  )}

                  <button
                    onClick={() => setPage((p) => Math.min(pageCount, p + 1))}
                    disabled={page === pageCount}
                    className="inline-flex h-8 w-8 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
                    aria-label="Next page"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}